import { DatePickerComponent } from '@syncfusion/ej2-react-calendars'
import { Input } from 'antd'
import StyledLoader from 'components/Elements/StyledLoader'
import GuideButtons from 'sections/Dashboard/features/SetupGuide/components/GuideButtons'
import { DataContext } from 'contexts/DataContextContainer'
import moment from 'moment'
import { useContext, useRef, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { EMAIL_RX } from 'services/constants'

function AuthorDetails({
  formData,
  currentAuthorData,
  setFormData,
  onNext,
  onPrevious,
  isWelcomeSent,
  isLoading,
}) {
  const { t } = useTranslation()
  const { lang } = useContext(DataContext)
  const datePickerRef = useRef(null)

  const [firstNameError, setFirstNameError] = useState(false)
  const [lastNameError, setLastNameError] = useState(false)
  const [emailError, setEmailError] = useState(false)
  const [confirmEmailError, setConfirmEmailError] = useState(false)

  if (isLoading || !formData) {
    return <StyledLoader />
  }

  const {
    firstName,
    lastName,
    email,
    emailConfirmation,
    startDate,
    purchaserIsAuthor,
    welcomeText,
  } = formData

  // Validate all fields and return true if valid.
  const validateForm = () => {
    if (purchaserIsAuthor || isWelcomeSent) {
      return true
    }

    let hasError = false

    if (!firstName) {
      hasError = true
      setFirstNameError(true)
    } else {
      setFirstNameError(false)
    }

    if (!lastName) {
      hasError = true
      setLastNameError(true)
    } else {
      setLastNameError(false)
    }

    if (EMAIL_RX.test(email) !== true) {
      hasError = true
      setEmailError(true)
    } else {
      setEmailError(false)
    }

    if (email !== emailConfirmation) {
      hasError = true
      setConfirmEmailError(true)
    } else {
      setConfirmEmailError(false)
    }

    return !hasError
  }

  return (
    <div className="card-body-container card-body-author-container">
      <div className="card card-body">
        {isWelcomeSent ? (
          <div className="progressbar-container">
            <div className="text-left mt-3">
              {/* TODO: Add i18n */}
              {purchaserIsAuthor ? (
                <h4>You are set to be the primary author.</h4>
              ) : (
                <h4>
                  The welcome email was sent to{' '}
                  <b>
                    {currentAuthorData.firstName} {currentAuthorData.lastName}
                  </b>{' '}
                  on{' '}
                  <b>
                    {moment(currentAuthorData.startDate).format('DD/MM/YY')}
                  </b>
                  .
                </h4>
              )}
            </div>
          </div>
        ) : (
          <div className="progressbar-container">
            <div className="text-left mt-3">
              <h4>{t('book_setup_guide_third_title')}</h4>
            </div>

            <div className="text-left mt-3 mb-5">
              <Row className="mb-3">
                <Col md={12} className="primary-author mt-2 mt-md-0">
                  <input
                    type="checkbox"
                    checked={purchaserIsAuthor}
                    onChange={() =>
                      setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        emailConfirmation: '',
                        startDate: '',
                        welcomeText: '',
                        purchaserIsAuthor: !purchaserIsAuthor,
                      })
                    }
                    data-testid="primary-author-checkbox"
                  />{' '}
                  {t('book_setup_guide_third_chk')}
                </Col>
              </Row>
              {!purchaserIsAuthor && (
                <>
                  <Row className="mb-3">
                    <Col md={{ span: 12 }}>
                      <label className="col-md-6">
                        {t('primary_author_gift')}
                      </label>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={{ span: 3 }}>
                      <label>{t('author_first_name')}</label>
                    </Col>
                    <Col md={{ span: 9 }}>
                      <Input
                        size="large"
                        className={
                          firstNameError ? 'form-control is-invalid' : ''
                        }
                        disabled={purchaserIsAuthor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        defaultValue={firstName}
                        placeholder={t('author_first_name')}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={{ span: 3 }}>
                      <label>{t('author_last_name')}</label>
                    </Col>
                    <Col md={{ span: 9 }}>
                      <Input
                        size="large"
                        className={
                          lastNameError ? 'form-control is-invalid' : ''
                        }
                        disabled={purchaserIsAuthor}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        defaultValue={lastName}
                        placeholder={t('author_last_name')}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={{ span: 3 }}>
                      <label>{t('author_email_address')}</label>
                    </Col>
                    <Col md={{ span: 9 }}>
                      <Input
                        size="large"
                        className={emailError ? 'form-control is-invalid' : ''}
                        disabled={purchaserIsAuthor}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        defaultValue={email}
                        placeholder={t('author_email_address')}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={{ span: 3 }}>
                      <label>{t('author_email_verify')}</label>
                    </Col>
                    <Col md={{ span: 9 }}>
                      <Input
                        size="large"
                        className={
                          confirmEmailError ? 'form-control is-invalid' : ''
                        }
                        disabled={purchaserIsAuthor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            emailConfirmation: e.target.value,
                          })
                        }
                        defaultValue={emailConfirmation}
                        placeholder={t('author_email_verify')}
                        onPaste={(e) => {
                          e.preventDefault()
                          return false
                        }}
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={7}>
                      <h4>{t('book_setup_guide_third_content')}</h4>
                    </Col>
                    <Col md={5}>
                      {!purchaserIsAuthor && (
                        <DatePickerComponent
                          placeholder="Choose a date"
                          ref={datePickerRef}
                          value={startDate}
                          firstDayOfWeek={1}
                          format="dd/MM/yyyy"
                          showClearButton={false}
                          showTodayButton={false}
                          change={(e) =>
                            setFormData({ ...formData, startDate: e.value })
                          }
                          close={() => {
                            if (datePickerRef) datePickerRef.current.focusOut()
                          }}
                          focus={() => {
                            if (datePickerRef) datePickerRef.current.show()
                          }}
                          locale={lang}
                        />
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <label>{t('book_setup_guide_third_textarea')}</label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        disabled={purchaserIsAuthor}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            welcomeText: e.target.value,
                          })
                        }
                        defaultValue={welcomeText}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <GuideButtons
        onNext={() => validateForm() && onNext()}
        onPrevious={onPrevious}
      />
    </div>
  )
}

export default AuthorDetails
